import React from 'react';
import { Box, Chip, FormControl, InputLabel, MenuItem, Select, Typography, FormHelperText } from '@mui/material';
import { useField } from 'formik';

const FormSelect = ({
    name,
    label,
    options = [], // Default to empty array if options is undefined
    required,
    multiple,
    value,
    onChange,
    disabled,
    error,
    helperText: customHelperText,
    ...props
}) => {
    // Utiliser useField de manière inconditionnelle
    // Si name n'est pas défini, on utilise un nom fictif qui ne sera pas utilisé
    const [field, meta] = useField(name || 'dummy-field');
    
    // Vérifier si on utilise Formik ou les props directes
    const isFormik = name !== undefined;
    
    // Détermine les valeurs à utiliser (Formik ou props directes)
    const inputValue = isFormik ? field.value : value;
    // If a custom onChange is provided, prefer it even when using Formik so the parent
    // can transform values (e.g., cast IDs to numbers). Fallback to Formik's handler.
    const handleChange = isFormik ? (onChange || field.onChange) : onChange;
    const inputError = isFormik ? (meta.touched && Boolean(meta.error)) : error;
    
    // Détermine le texte d'aide à afficher
    const displayHelperText = isFormik && meta.touched && meta.error 
        ? meta.error 
        : customHelperText;
        
    // Ensure options is always an array
    const safeOptions = Array.isArray(options) ? options : [];

    // DEBUG: Log current value and option id types
    try {
        // Use console.debug to reduce noise in production logs
        console.debug('[FormSelect] render', {
            name,
            value: inputValue,
            valueType: Array.isArray(inputValue) ? 'array' : typeof inputValue,
            isFormik,
            optionSample: safeOptions.slice(0, 3).map(o => ({ id: o.id, idType: typeof o.id, title: o.title })),
        });
    } catch (_) { /* noop */ }

    const handleChangeWrapped = (e) => {
        try {
            const selectedRaw = e?.target?.value;
            const selected = multiple
                ? (Array.isArray(selectedRaw) ? selectedRaw : (selectedRaw != null ? [selectedRaw] : []))
                : selectedRaw;
            console.debug('[FormSelect] onChange', {
                name,
                selected,
                selectedType: Array.isArray(selected) ? 'array' : typeof selected,
                isFormik,
            });
            // When multiple, synthesize an event with coerced array value to keep formik/parent consistent
            if (multiple) {
                const syntheticEvent = { target: { name, value: selected } };
                return handleChange?.(syntheticEvent);
            }
        } catch (_) { /* noop */ }
        return handleChange?.(e);
    };

    return (
        <FormControl
            fullWidth
            required={required}
            disabled={disabled}
            error={inputError}
        >
            <InputLabel>{label}</InputLabel>
            <Select
                name={name}
                label={label}
                value={multiple
                    ? (Array.isArray(inputValue) ? inputValue : (inputValue !== undefined && inputValue !== null ? [inputValue] : []))
                    : (inputValue !== undefined && inputValue !== null ? inputValue : '')}
                multiple={multiple}
                onChange={handleChangeWrapped}
                renderValue={(selected) =>
                    multiple ? (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {(Array.isArray(selected) ? selected : (selected != null ? [selected] : [])).map((value) => {
                                const selectedOption = safeOptions.find((option) => option.id === value);
                                return selectedOption ? (
                                    <Chip key={value} label={selectedOption.title} />
                                ) : null;
                            })}
                        </Box>
                    ) : (
                        (() => {
                            const opt = safeOptions.find((option) => option.id === selected);
                            return (opt && (opt.selectedTitle ?? opt.title)) || '';
                        })()
                    )
                }
            >
                {safeOptions.length === 0 ? (
                    <MenuItem disabled>
                        <Typography color="textSecondary">No options available</Typography>
                    </MenuItem>
                ) : (
                    safeOptions.map((option) => (
                        <MenuItem key={option.id} value={option.id}>
                            {option.title}
                        </MenuItem>
                    ))
                )}
            </Select>
            {displayHelperText && (
                <FormHelperText>{displayHelperText}</FormHelperText>
            )}
        </FormControl>
    );
};

export default FormSelect;